import { Button, Card } from "react-bootstrap";
import { toast } from "react-toastify";

import { FileText, ExternalLink, FileTextIcon, Check, X } from "lucide-react";
import { DateTime } from "luxon";
import { confirmDialog } from "primereact/confirmdialog";
import { twMerge } from "tailwind-merge";

import { getDateFromIso } from "@/_helpers/stringHelper";
import { useUpdateEstimateStatus } from "@/_services/hooks/invoice.hooks";
import { useGetUserType } from "@/modules/auth/hooks/userType.hooks";
import { getReminderDaysInterval } from "@/modules/order/components/OrderInvoiceInfosPreview/utils/invoice-reminder.utils";

import { CustomButtonLink } from "./CustomButtonLink";
import { ReminderBox } from "./ReminderBox";
import { StatusTag } from "./StatusTag";

interface OrderEstimatesProps {
  estimate: any;
  serviceId?: string;
}

const buildPennylaneInvoiceUrl = (
  invoiceId: number,
  hasRelatedInvoices: boolean = false
) => {
  const companyId = hasRelatedInvoices
    ? import.meta.env.VITE_PENNYLANE_LEGIMETRIE_COMPANY_ID
    : import.meta.env.VITE_PENNYLANE_PHOENIX_COMPANY_ID;

  const currentYear = new Date().getFullYear();
  const startDate = `${currentYear - 1}-06-30`;
  const endDate = `${currentYear}-06-30`;

  const filter = encodeURIComponent(
    `[{"field":"date","operator":"gteq","value":"${startDate}"},{"field":"date","operator":"lteq","value":"${endDate}"}]`
  );

  return `https://app.pennylane.com/companies/${companyId}/clients/customer_invoices?filter=${filter}&invoice_id=${invoiceId}&page=1&sort=-date%2C%2Bid&subtab=all`;
};

const OrderEstimates: React.FC<OrderEstimatesProps> = ({ estimate, serviceId }) => {
  const { isSuperAdmin } = useGetUserType();

  const updateEstimateStatus = useUpdateEstimateStatus({
    handleSuccess: (data) => {
      const actionText = data?.status === "accepted" ? "accepté" : "refusé";
      toast.success(`Le devis a été ${actionText} avec succès`);
    },
    handleError: (error) => {
      console.error("Error updating estimate status:", error);
      toast.error("Une erreur est survenue lors de la mise à jour du devis");
    },
  });

  const handleEstimateAction = (action: "accepted" | "reject") => {
    if (!serviceId || !estimate?.v2_id) {
      toast.error("Informations manquantes pour mettre à jour le devis");
      return;
    }

    const actionText = action === "accepted" ? "accepter" : "refuser";
    const estimateNumber = estimate?.estimate_number || estimate?.label || estimate?.id;

    confirmDialog({
      message: `Voulez-vous vraiment ${actionText} le devis ${estimateNumber} ?`,
      header: `Confirmation - ${action === "accepted" ? "Accepter" : "Refuser"} le devis`,
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: action === "accepted" ? "p-button-success" : "p-button-danger",
      acceptLabel: "Oui",
      rejectLabel: "Non",
      accept: () => {
        const status = action === "accepted" ? "accepted" : "denied";
        updateEstimateStatus.mutate({
          quoteId: estimate.id,
          serviceId,
          status,
        });
      },
    });
  };
  const amountBeforeTax = estimate?.currency_amount_before_tax;
  const estimateDeadlineDate = estimate?.deadline;
  const isEstimatePending = estimate?.status === "pending";
  const isEstimateAccepted = estimate?.status === "accepted";
  const isEstimateRejected = estimate?.status === "denied";
  const isEstimateInvoiced = estimate?.status === "invoiced";

  const hasRelatedInvoices =
    estimate?.related_invoices && estimate.related_invoices.length > 0;
  const relatedInvoice = hasRelatedInvoices
    ? estimate.related_invoices[0]
    : null;

  const reminderDaysInterval = getReminderDaysInterval({
    endDate: estimateDeadlineDate,
  });

  const paymentNotLateWithoutReminder = reminderDaysInterval < -2;

  const getReminderPeriod = () => {
    if (!isEstimatePending) {
      return undefined;
    }

    // Show reminders at specific day intervals for pending estimates
    if (reminderDaysInterval >= -2 && reminderDaysInterval <= 0) {
      return <ReminderBox text="J-2" />;
    } else if (reminderDaysInterval >= 1 && reminderDaysInterval <= 6) {
      return (
        <ReminderBox
          className="bg-orange-FF7A00/10 text-orange-FF7A00"
          text="J+1"
        />
      );
    } else if (reminderDaysInterval >= 7 && reminderDaysInterval <= 13) {
      return (
        <ReminderBox
          className="bg-orange-FF7A00/10 text-orange-FF7A00"
          text="J+7"
        />
      );
    } else if (reminderDaysInterval === 14) {
      return (
        <ReminderBox
          className="bg-orange-FF7A00/10 text-orange-FF7A00"
          text="J+14"
        />
      );
    } else if (reminderDaysInterval > 14) {
      return (
        <ReminderBox
          className="bg-orange-FF7A00/10 text-orange-FF7A00"
          text={`Devis en attente depuis J+${reminderDaysInterval}`}
        />
      );
    }

    return undefined;
  };

  const customerHasBeenReminded = !!getReminderPeriod();

  return (
    <div className="max-w-sm mx-auto">
      <Card className="border-0 shadow-md bg-white">
        <Card.Header>
          <Card.Title className="flex items-center space-x-2">
            <FileTextIcon className="w-4 h-4 text-blue-600" />
            <span className="text-md">Détails du devis</span>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {/* Estimate Details */}
          <div className="bg-gray-50 rounded-lg p-3 mb-2 space-y-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Numéro de devis
                </label>
                <p className="text-md font-bold text-gray-900 mt-1">
                  {estimate?.estimate_number || estimate?.label || estimate?.id}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Montant
                </label>
                <p className="text-md font-bold text-gray-900 mt-1">
                  {amountBeforeTax
                    ? `${Number(amountBeforeTax).toFixed(2).toString().replace(".", ",")} € HT`
                    : ""}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Date d'émission
                </label>
                <p className="text-md font-medium text-gray-900 mt-1">
                  {getDateFromIso(estimate?.date, DateTime.DATE_SHORT)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Date d'échéance
                </label>
                <p className="text-md font-medium text-gray-900 mt-1">
                  {isEstimateAccepted || isEstimateRejected
                    ? getDateFromIso(estimate?.updated_at, DateTime.DATE_SHORT)
                    : paymentNotLateWithoutReminder
                      ? getDateFromIso(
                          estimateDeadlineDate,
                          DateTime.DATE_SHORT
                        )
                      : "En attente"}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-start pt-2">
              <label className="text-sm font-medium text-gray-600">
                Statut
              </label>
              <div className="mt-1">
                <StatusTag
                  className={twMerge(
                    "font-semibold px-3 py-1",
                    (() => {
                      if (isEstimatePending) {
                        return "bg-orange-100 text-orange-700";
                      } else if (isEstimateAccepted) {
                        return "bg-green-100 text-green-700";
                      } else if (isEstimateRejected) {
                        return "bg-red-100 text-red-700";
                      } else if (isEstimateInvoiced) {
                        return "bg-blue-100 text-blue-700";
                      } else {
                        return "bg-gray-100 text-gray-700";
                      }
                    })()
                  )}
                  text={(() => {
                    if (isEstimatePending) {
                      return "En attente";
                    } else if (isEstimateAccepted) {
                      return "Accepté";
                    } else if (isEstimateRejected) {
                      return "Refusé";
                    } else if (isEstimateInvoiced) {
                      return "Facturé";
                    } else {
                      return estimate?.status || "Inconnu";
                    }
                  })()}
                />
              </div>
            </div>

            {/* Reminder Box */}
            {customerHasBeenReminded && (
              <div className="pt-1">
                <label className="text-sm font-medium text-gray-600">
                  Dernière relance
                </label>
                <div className="mt-1">{getReminderPeriod()}</div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-1">
              {isEstimatePending && (
               <div className="flex gap-3">
                  <Button 
                    onClick={() => handleEstimateAction("accepted")} 
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm border-0"
                    disabled={updateEstimateStatus.isLoading}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Accepter le devis
                  </Button>
                  <Button
                    onClick={() => handleEstimateAction("reject")}
                    variant="outline"
                    className="flex-1 text-red-600 border-red-600 hover:bg-red-50 bg-transparent text-sm"
                    disabled={updateEstimateStatus.isLoading}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Refuser le devis
                  </Button>
               </div>
            )}

            <Button
              variant="outline"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-1 px-2 rounded-lg flex items-center justify-center gap-1 transition-colors duration-200"
              onClick={() => window.open(estimate?.file_url, "_blank")}
            >
              <FileText className="w-4 h-4" />
              Télécharger le devis en PDF
            </Button>

            {isEstimatePending && estimate?.public_url && (
              <CustomButtonLink
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg flex items-center justify-center gap-1 transition-colors duration-200"
                to={estimate?.public_url}
                text="Accepter le devis"
              />
            )}

          
            {isSuperAdmin && (
              <Button
                variant="outline"
                className="w-full border border-purple-200 text-purple-600 hover:bg-purple-50 py-1 px-2 rounded-lg flex items-center justify-center gap-1 transition-colors duration-200"
                onClick={() => {
                  if (hasRelatedInvoices && relatedInvoice?.v2_id) {
                    window.open(
                      buildPennylaneInvoiceUrl(relatedInvoice.v2_id, true),
                      "_blank"
                    );
                  } else {
                    const companyId = import.meta.env
                      .VITE_PENNYLANE_PHOENIX_COMPANY_ID;
                    window.open(
                      `https://app.pennylane.com/companies/${companyId}/clients/customer_estimates?estimate_id=${estimate?.v2_id || estimate?.id}`,
                      "_blank"
                    );
                  }
                }}
              >
                <ExternalLink className="w-4 h-4" />
                {hasRelatedInvoices
                  ? "Ouvrir la facture dans Pennylane"
                  : "Ouvrir le devis dans Pennylane"}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default OrderEstimates;
